import { z } from 'zod';

/** `'all'` is the wildcard value — the article applies regardless of the searcher's country. */
export const ARTICLE_COUNTRIES = ['all', 'au', 'nz'] as const;
export const zArticleCountry = z.literal(ARTICLE_COUNTRIES);
export type ArticleCountry = z.output<typeof zArticleCountry>;

/**
 * RAG-search filters applied to this article. Single value per filter, with `'all'` meaning
 * "applies regardless".
 *
 * Limitation: `@convex-dev/rag` requires every declared filter name to have exactly one value
 * per entry, and search filters across distinct names are OR'd (not AND'd). The `'all'`
 * wildcard satisfies the per-entry-required-value rule while the search side OR's the
 * specific context value with `'all'` to keep wildcard-tagged articles surfacing.
 */
export const articleFiltersSchema = z.object({
  country: zArticleCountry.default('all'),
});
export type ArticleFilters = z.output<typeof articleFiltersSchema>;

export const articleSchema = z.object({
  title: z.string(),
  /** Markdown content. */
  body: z.string(),
  filters: articleFiltersSchema.default(() => articleFiltersSchema.decode({})),
  /** Set by `_indexArticle` after a successful `rag.add`. Null/older than `updatedAt` means the RAG index is stale. */
  lastIndexedAt: z.number().nullable().default(null),
  // TODO: Add websiteConfig with nested fields such as "slug", "category" etc

  updatedAt: z.number().optional(),
  _creationTime: z.number().optional(),
  _id: z.string().optional(),
});
export type Article = z.output<typeof articleSchema>;

/** Strict schema (Convex args / form validation). */
export const articleStrictSchema = articleSchema.safeExtend({
  title: z.string().min(1),
  body: z.string().min(1),
});

/** Convex stored shape. */
export const articleCvxSchema = articleSchema.safeExtend({
  updatedAt: z.number(),
  _creationTime: z.number(),
  _id: z.string(),
});
export type ArticleCvx = z.output<typeof articleCvxSchema>;
